import React, { isValidElement, useEffect, useMemo, useState } from "react";
import { FieldControl, SearchInput } from "spark-core-dx/components";
import ResourceStateControl from "./ResourceStateControl";
import "./resourceCatalogPanel.scss";

const joinClassNames = (...classNames) =>
  classNames
    .flat()
    .filter(Boolean)
    .join(" ");

const normalizeItems = (items) => Array.isArray(items) ? items : [];

const readFilterValue = (filter) => filter?.value ?? "";

const toDomId = (value) =>
  String(value ?? "")
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    || "tab";

const filterMatchesItem = (filter, item) => {
  if (!filter?.matchesItem) return true;
  return filter.matchesItem(item, readFilterValue(filter));
};

const renderFilterControl = (filter, key, ariaLabel) => (
  <FieldControl
    key={key}
    fieldMetadata={filter.fieldMetadata}
    value={readFilterValue(filter)}
    onChange={(nextValue) => filter.onChange?.(nextValue ?? filter.defaultValue ?? "")}
    disabled={filter.disabled}
    disableError
    aria-label={filter.ariaLabel || ariaLabel}
  />
);

const renderSearchControl = (filter) => {
  if (filter.controlType === "field") {
    return renderFilterControl(filter, "search", filter.ariaLabel || filter.fieldMetadata?.AriaLabel || filter.fieldMetadata?.ariaLabel || "Search");
  }

  return (
    <SearchInput
      key="search"
      className="resource-catalog-list-tab__search"
      value={readFilterValue(filter)}
      onChange={(nextValue) => filter.onChange?.(nextValue ?? filter.defaultValue ?? "")}
      disabled={filter.disabled}
      placeholder={
        filter.placeholder ||
        filter.fieldMetadata?.PlaceHolder ||
        filter.fieldMetadata?.Placeholder ||
        filter.fieldMetadata?.placeHolder ||
        filter.fieldMetadata?.placeholder ||
        "Search..."
      }
      titleTag={filter.ariaLabel || filter.fieldMetadata?.AriaLabel || filter.fieldMetadata?.ariaLabel || "Search"}
    />
  );
};

const renderMetaItem = (item, index) => {
  if (item === undefined || item === null || item === "") return null;
  if (isValidElement(item)) return item;

  if (typeof item === "object") {
    const key = item.key ?? index;
    const content = item.content ?? item.children ?? item.label ?? item.value ?? item.text;
    if (content === undefined || content === null || content === "") return null;

    return (
      <span key={key} className={joinClassNames("resource-catalog-row__meta-item", item.className)}>
        {content}
      </span>
    );
  }

  return (
    <span key={index} className="resource-catalog-row__meta-item">
      {item}
    </span>
  );
};

export const ResourceCatalogPanel = ({
  title,
  summary,
  tabs = [],
  activeTabKey,
  onTabChange,
  idPrefix,
  className,
  ariaLabel
}) => {
  const [uncontrolledTabKey, setUncontrolledTabKey] = useState(activeTabKey || tabs[0]?.key);
  const isControlled = activeTabKey !== undefined;
  const selectedTabKey = isControlled ? activeTabKey : uncontrolledTabKey;
  const activeIndex = Math.max(0, tabs.findIndex((tab) => tab.key === selectedTabKey));
  const activeTab = tabs[activeIndex] || tabs[0];
  const showTabs = tabs.length > 1;
  const tabIdPrefix = toDomId(idPrefix || `${title || "resource"}-catalog`);
  const tabPanelId = activeTab ? `${tabIdPrefix}-panel-${toDomId(activeTab.key)}` : undefined;
  const activeTabId = activeTab ? `${tabIdPrefix}-tab-${toDomId(activeTab.key)}` : undefined;

  useEffect(() => {
    if (isControlled) return;
    if (!tabs.length) {
      setUncontrolledTabKey(undefined);
      return;
    }
    if (tabs.some((tab) => tab.key === uncontrolledTabKey)) return;
    setUncontrolledTabKey(tabs[0].key);
  }, [isControlled, tabs, uncontrolledTabKey]);

  const handleTabClick = (tab) => {
    if (!isControlled) {
      setUncontrolledTabKey(tab.key);
    }
    onTabChange?.(tab.key, tab);
  };

  return (
    <section className={joinClassNames("resource-workspace-panel", "resource-catalog-panel", className)}>
      <div className="resource-catalog-panel__header">
        <div className="resource-catalog-panel__tab-row">
          <div className="resource-catalog-panel__title">
            <h2>{title}</h2>
            {summary ? <p>{summary}</p> : null}
          </div>
          {showTabs ? (
            <div className="resource-catalog-panel__tabs" role="tablist" aria-label={ariaLabel || `${title || "Resource"} catalog`}>
              {tabs.map((tab) => {
                const isActive = tab.key === activeTab?.key;
                const tabId = `${tabIdPrefix}-tab-${toDomId(tab.key)}`;
                const panelId = `${tabIdPrefix}-panel-${toDomId(tab.key)}`;
                return (
                  <button
                    key={tab.key}
                    id={tabId}
                    type="button"
                    role="tab"
                    aria-selected={isActive ? "true" : "false"}
                    aria-controls={panelId}
                    className={joinClassNames("resource-catalog-panel__tab", isActive ? "is-active" : "")}
                    onClick={() => handleTabClick(tab)}
                  >
                    {tab.title}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
      {activeTab ? (
        <div
          id={tabPanelId}
          className="resource-catalog-panel__content"
          role="tabpanel"
          aria-labelledby={showTabs ? activeTabId : undefined}
          aria-label={!showTabs ? activeTab.title : undefined}
        >
          {activeTab.content}
        </div>
      ) : null}
    </section>
  );
};

export const ResourceCatalogListTab = ({
  items = [],
  getItemKey,
  renderItem,
  search,
  statusFilter,
  filters = [],
  searchPlacement = "start",
  showFilters = true,
  ariaLabel,
  emptyMessage = "No items have been loaded yet.",
  emptyFilteredMessage = "No items match these filters.",
  className
}) => {
  const sourceItems = useMemo(() => normalizeItems(items), [items]);
  const extraFilters = useMemo(() => normalizeItems(filters), [filters]);
  const filteredItems = useMemo(
    () => sourceItems
      .filter((item) => filterMatchesItem(statusFilter, item))
      .filter((item) => extraFilters.every((filter) => filterMatchesItem(filter, item)))
      .filter((item) => filterMatchesItem(search, item)),
    [extraFilters, search, sourceItems, statusFilter]
  );
  const hasFilters = Boolean(search || statusFilter || extraFilters.length);
  const emptyText = sourceItems.length > 0 ? emptyFilteredMessage : emptyMessage;

  return (
    <div className={joinClassNames("resource-catalog-list-tab", className)}>
      {hasFilters && showFilters ? (
        <div className="resource-catalog-list-tab__filters" aria-label={`${ariaLabel || "Resource catalog"} filters`}>
          {search && searchPlacement !== "end" ? (
            renderSearchControl(search)
          ) : null}
          {extraFilters.map((filter, index) =>
            renderFilterControl(filter, filter.key || `filter-${index}`, `${ariaLabel || "Resource catalog"} filter ${index + 1}`)
          )}
          {statusFilter ? (
            renderFilterControl(statusFilter, "status", "Status")
          ) : null}
          {search && searchPlacement === "end" ? (
            renderSearchControl(search)
          ) : null}
        </div>
      ) : null}

      {filteredItems.length > 0 ? (
        <div className="resource-catalog-list-tab__list" aria-label={ariaLabel}>
          {filteredItems.map((item, index) => (
            <React.Fragment key={getItemKey?.(item, index) ?? index}>
              {renderItem?.(item, index)}
            </React.Fragment>
          ))}
        </div>
      ) : (
        <ResourceStateControl
          className="resource-catalog-list-tab__empty"
          compact
          message={emptyText}
        />
      )}
    </div>
  );
};

export const ResourceCatalogListControl = ({
  items = [],
  getItemKey,
  renderItem,
  search,
  statusFilter,
  filters = [],
  ariaLabel,
  emptyMessage = "No items have been loaded yet.",
  emptyFilteredMessage = "No items match these filters.",
  summary,
  actions,
  className,
  listClassName
}) => {
  const sourceItems = useMemo(() => normalizeItems(items), [items]);
  const extraFilters = useMemo(() => normalizeItems(filters), [filters]);
  const filteredItems = useMemo(
    () => sourceItems
      .filter((item) => filterMatchesItem(statusFilter, item))
      .filter((item) => extraFilters.every((filter) => filterMatchesItem(filter, item)))
      .filter((item) => filterMatchesItem(search, item)),
    [extraFilters, search, sourceItems, statusFilter]
  );
  const hasToolbar = Boolean(summary || search || actions);
  const emptyText = sourceItems.length > 0 ? emptyFilteredMessage : emptyMessage;
  const toolbarSearch = search
    ? {
      ...search,
      placeholder: search.placeholder || "Search..."
    }
    : null;

  return (
    <div className={joinClassNames("resource-catalog-list-control", className)}>
      {hasToolbar ? (
        <div className="resource-catalog-list-control__toolbar">
          {summary ? <span className="resource-catalog-list-control__summary">{summary}</span> : <span aria-hidden="true" />}
          <div className="resource-catalog-list-control__actions">
            {toolbarSearch ? renderSearchControl(toolbarSearch) : null}
            {actions}
          </div>
        </div>
      ) : null}
      <ResourceCatalogListTab
        className={listClassName}
        items={filteredItems}
        getItemKey={getItemKey}
        renderItem={renderItem}
        ariaLabel={ariaLabel}
        emptyMessage={emptyText}
        emptyFilteredMessage={emptyFilteredMessage}
      />
    </div>
  );
};

export const ResourceCatalogRow = ({
  title,
  subtitle,
  metaItems = [],
  actions,
  className,
  titleAttribute,
  onClick,
  ariaLabel
}) => {
  const renderedMetaItems = normalizeItems(metaItems)
    .map(renderMetaItem)
    .filter(Boolean);
  const interactive = typeof onClick === "function";
  const onKeyDown = interactive
    ? (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      onClick(event);
    }
    : undefined;

  return (
    <article
      className={joinClassNames("resource-catalog-row", interactive ? "resource-catalog-row--interactive" : "", className)}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? ariaLabel || (typeof title === "string" ? title : undefined) : undefined}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <span className="resource-catalog-row__main">
        <strong title={titleAttribute ?? (typeof title === "string" ? title : undefined)}>{title}</strong>
        {subtitle ? <small>{subtitle}</small> : null}
      </span>
      {renderedMetaItems.length > 0 ? (
        <span className="resource-catalog-row__meta">{renderedMetaItems}</span>
      ) : null}
      {actions ? (
        <span className="resource-catalog-row__actions">{actions}</span>
      ) : null}
    </article>
  );
};

export default ResourceCatalogPanel;
